import { useEffect, useState } from "react";
import { Link } from "react-router";
import { authedFetch } from "../../lib/api-client";
import type { ApiEnvelope } from "@a2/core/schemas";
import { useT } from "../../lib/use-t";

type RecentSessionsResponse = {
  sessions: Array<{
    id: string;
    finishedAt: number | null;
    percentage: number | null;
  }>;
};

const SPARK_HEIGHT = 56;

/**
 * Voortgang mini-card: a 7-bar percentage sparkline (last bar in --warm)
 * plus the mean of the shown points. Reuses the same /sessions/recent
 * endpoint and decoding shape as TrendStrip — a static 7-bar strip needs
 * no chart library, so this stays out of recharts to keep the bundle lean.
 */
export function VoortgangCard() {
  const t = useT();
  const [points, setPoints] = useState<number[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch("/sessions/recent");
        const json = (await res.json()) as ApiEnvelope<RecentSessionsResponse>;
        if (!json.ok || cancelled) {
          if (!cancelled) setPoints([]);
          return;
        }
        // Same decoding as TrendStrip: finished sessions with a percentage,
        // chronological order, last 7.
        const pcts = [...json.data.sessions]
          .filter((s) => s.percentage != null && s.finishedAt != null)
          .reverse()
          .map((s) => Math.round((s.percentage ?? 0) * 100))
          .slice(-7);
        setPoints(pcts);
      } catch {
        if (!cancelled) setPoints([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] p-5 shadow-[var(--shadow)] sm:p-[22px]">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="text-[17px] font-semibold tracking-tight">{t("vandaag.voortgang.heading")}</h3>
        <Link
          to="/voortgang"
          className="rounded-[var(--radius-sm)] px-3 py-1.5 text-sm font-semibold text-[var(--accent)] transition-colors hover:bg-[var(--accent-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
        >
          {t("vandaag.voortgang.viewAll")}
        </Link>
      </div>
      {points === null ? (
        <div
          className="animate-pulse rounded bg-[var(--panel-soft)]/60"
          style={{ height: SPARK_HEIGHT }}
          aria-hidden
        />
      ) : points.length === 0 ? (
        <p
          className="flex items-center font-serif-italic text-[var(--ink-muted)]"
          style={{ minHeight: SPARK_HEIGHT }}
        >
          {t("vandaag.voortgang.empty")}
        </p>
      ) : (
        <>
          <div className="flex items-end gap-1.5" style={{ height: SPARK_HEIGHT }} aria-hidden>
            {points.map((pct, i) => {
              const last = i === points.length - 1;
              return (
                <div
                  key={i}
                  className={`flex-1 rounded-t-[4px] rounded-b-[2px] ${
                    last ? "bg-[var(--warm)]" : "bg-[var(--accent)] opacity-85"
                  }`}
                  style={{ height: `${Math.max(pct, 4)}%` }}
                />
              );
            })}
          </div>
          <div className="mt-3.5 flex items-baseline gap-2">
            <span className="font-display text-[26px] font-semibold tabular-nums">
              {Math.round(points.reduce((a, b) => a + b, 0) / points.length)}
            </span>
            <span className="text-[13px] text-[var(--ink-muted)]">
              {t("vandaag.voortgang.avgLabelFmt", { count: points.length })}
            </span>
          </div>
        </>
      )}
    </section>
  );
}
